(function () {
  // Config
  const REBUY_WIDGET_ID = '227065';
  const BOUQUET_PROPERTY = 'Bouquet Name';
  const BOUQUET_ID_PROPERTY = '_bouquet_id';
  const BOUQUET_INPUT_ID = 'rebuy-bouquet-name';
  const BOUQUET_DEFAULT_NAME = 'My Bouquet #1';

  let rebuyBundleTimeout;

  // Helper: fetch current cart
  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'include' });
    if (!res.ok) throw new Error('Error fetching cart');
    return res.json();
  }

  // Helper: async sleep
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Reads the current bouquet name (fallbacks to default)
  function getCurrentBouquetName() {
    const input = document.getElementById(BOUQUET_INPUT_ID);
    const value = input?.value?.trim();
    return value || BOUQUET_DEFAULT_NAME;
  }

  // After a bouquet is added, increments the input value
  // so the user doesn't accidentally reuse the same name
  function incrementBouquetName(previousName) {
    const input = document.getElementById(BOUQUET_INPUT_ID);
    if (!input) return;

    const current = input.value.trim();
    const prev = previousName.trim();

    // If user manually edits the value after adding, do not overwrite
    if (!current || current.toLowerCase() !== prev.toLowerCase()) {
      return;
    }

    // Detect if name already ends in #n
    const match = current.match(/^(.*?)(?:\s#(\d+))?$/);

    const baseName = (match && match[1] ? match[1] : current).trim();
    const currentNumber = match && match[2] ? parseInt(match[2], 10) || 1 : 1;

    const nextNumber = currentNumber + 1;
    input.value = `${baseName} #${nextNumber}`;
  }

  function generateUniqueBouquetName(desiredName, cart) {
    const normalize = (str) => str.trim().toLowerCase();

    const existingNames = cart.items
      .map((item) => item.properties || {})
      .map((props) => props[BOUQUET_PROPERTY])
      .filter(Boolean)
      .map(normalize);

    const desiredNormalized = normalize(desiredName);

    // If name is not used yet, keep it as is
    if (!existingNames.includes(desiredNormalized)) {
      return desiredName.trim();
    }

    // If name is already used, append / increment #n
    const match = desiredName.match(/^(.*?)(?:\s#(\d+))?$/);
    const baseName = (match && match[1] ? match[1] : desiredName).trim();
    let number = match && match[2] ? parseInt(match[2], 10) || 1 : 1;

    let candidate;
    do {
      number += 1;
      candidate = `${baseName} #${number}`;
    } while (existingNames.includes(normalize(candidate)));

    return candidate;
  }

  // Updates a line item and retries if Shopify returns 409 conflict
  async function updateLineItemBouquet(item, bouquetName, bouquetUniqueId) {
    const props = item.properties || {};

    const newProps = {
      ...props,
      [BOUQUET_PROPERTY]: bouquetName,
      [BOUQUET_ID_PROPERTY]: bouquetUniqueId
    };

    const payload = {
      id: item.key,
      quantity: item.quantity,
      properties: newProps
    };

    let attempt = 0;
    const maxAttempts = 3;

    while (attempt < maxAttempts) {
      attempt++;

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          return res.json();
        }

        // Retry on 409 (conflict) or temporary 5xx errors
        if (res.status === 409 || (res.status >= 500 && res.status <= 599)) {
          console.warn(
            `cart/change.js conflict (status ${res.status}), attempt ${attempt} of ${maxAttempts}`
          );
          await sleep(300 * attempt); // lightweight exponential backoff
          continue;
        }

        // Log unexpected errors and exit
        const errorData = await res.json().catch(() => ({}));
        console.error('Error in cart/change.js:', res.status, errorData);
        break;
      } catch (err) {
        console.error('Fetch failure in cart/change.js, attempt', attempt, err);
        await sleep(300 * attempt);
      }
    }
  }

  // Assigns Bouquet Name and unique id to all Rebuy items missing it
  async function tagRebuyItemsWithoutBouquetName(bouquetName) {
    if (!bouquetName) return;

    const cart = await getCart();

    const uniqueBouquetName = generateUniqueBouquetName(bouquetName, cart);

    const rebuyItemsWithoutName = cart.items.filter((item) => {
      const props = item.properties || {};

      const isRebuy =
        props._source === 'Rebuy' &&
        props._widget_id === REBUY_WIDGET_ID;

      const hasBouquetName = Object.prototype.hasOwnProperty.call(
        props,
        BOUQUET_PROPERTY
      );

      return isRebuy && !hasBouquetName;
    });

    if (!rebuyItemsWithoutName.length) return;

    // Unique bundle id (timestamp + random)
    const bouquetUniqueId = `${Date.now()}-${Math.floor(Math.random() * 10000)}`;

    // Update one by one (no Promise.all) to avoid conflicts
    for (const item of rebuyItemsWithoutName) {
      await updateLineItemBouquet(item, uniqueBouquetName, bouquetUniqueId);
    }

    const updatedCart = await getCart();
    const evt = new CustomEvent('cart:updated', { detail: { cart: updatedCart } });
    document.dispatchEvent(evt);

    // Automatically increment the bouquet name in the UI for the next bundle
    incrementBouquetName(uniqueBouquetName);
  }

  // Rebuy event when bundle is added to the cart
  document.addEventListener('rebuy.addBundleToCart', function () {
    const bouquetName = getCurrentBouquetName();

    // Prevent multiple executions if event fires more than once
    clearTimeout(rebuyBundleTimeout);

    // Allow Rebuy to finish writing to cart before tagging properties
    rebuyBundleTimeout = setTimeout(() => {
      tagRebuyItemsWithoutBouquetName(bouquetName).catch((err) => {
        console.error('Error tagging rebuy items with bouquet_name:', err);
      });
    }, 700); // Adjustable depending on widget performance
  });

  // Rebuy widget ready: inject input field UI
  document.addEventListener('rebuy.ready', function () {
    const headerWrapperContainer = document.querySelector(
      '.rebuy-bundle-builder__step-header-copy-container'
    );

    if (!headerWrapperContainer) return;

    // Avoid injecting the UI twice (Rebuy fires ready twice sometimes)
    if (headerWrapperContainer.querySelector(`#${BOUQUET_INPUT_ID}`)) {
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'rebuy-bouquet-name-wrapper';

    const label = document.createElement('label');
    label.setAttribute('for', BOUQUET_INPUT_ID);
    label.textContent = 'Bouquet name';

    const input = document.createElement('input');
    input.type = 'text';
    input.id = BOUQUET_INPUT_ID;
    input.name = 'rebuy_bouquet_name';
    input.value = BOUQUET_DEFAULT_NAME;

    const helpText = document.createElement('p');
    helpText.className = 'rebuy-bouquet-name-help-text';
    helpText.textContent =
      'Give this bouquet a name so you can easily recognize it in your cart and order.';

    wrapper.appendChild(label);
    wrapper.appendChild(input);
    wrapper.appendChild(helpText);

    headerWrapperContainer.appendChild(wrapper);
  });
})();